import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import MobileNav from "@/components/MobileNav";
import { Search, Heart, ArrowLeft, Info, Check, Plus } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const PortfolioPage = () => {
  const [portfolio, setPortfolio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [board, setBoard] = useState(null);

  useEffect(() => {
    fetchPortfolio();
    fetchBoard();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPortfolio();
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchPortfolio = async () => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : "";
      const response = await axios.get(`${API}/portfolio${params}`, { withCredentials: true });
      setPortfolio(response.data);
    } catch (error) {
      console.error("Error fetching portfolio:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBoard = async () => {
    try {
      const response = await axios.get(`${API}/board/current`, { withCredentials: true });
      setBoard(response.data);
    } catch (error) {
      console.error("Error fetching board:", error);
    }
  };

  const handleLike = async (portfolioId) => {
    try {
      await axios.post(`${API}/portfolio/${portfolioId}/like`, {}, { withCredentials: true });
      setPortfolio((prev) =>
        prev.map((p) =>
          p.portfolio_id === portfolioId ? { ...p, likes: (p.likes || 0) + 1 } : p
        )
      );
    } catch (error) {
      console.error("Error liking:", error);
    }
  };

  const handleSelect = async (portfolioId) => {
    if (!board) {
      toast.error("Please check in first to select designs");
      return;
    }

    if (board.status !== "editing") {
      toast.error("Your appointment is locked. Chat with your tech for changes.");
      return;
    }

    const isAlreadySelected = board.selected_designs?.some(d => d.portfolio_id === portfolioId);

    try {
      if (isAlreadySelected) {
        await axios.post(
          `${API}/board/${board.board_id}/remove-design?portfolio_id=${portfolioId}`,
          {},
          { withCredentials: true }
        );
        setBoard(prev => ({
          ...prev,
          selected_designs: prev.selected_designs.filter(d => d.portfolio_id !== portfolioId)
        }));
        toast.success("Design removed");
      } else {
        await axios.post(
          `${API}/board/${board.board_id}/add-design?portfolio_id=${portfolioId}`,
          {},
          { withCredentials: true }
        );
        const selectedDesign = portfolio.find(p => p.portfolio_id === portfolioId);
        setBoard(prev => ({
          ...prev,
          selected_designs: [...(prev.selected_designs || []), {
            portfolio_id: selectedDesign.portfolio_id,
            title: selectedDesign.title,
            image_url: selectedDesign.image_url,
            tagged_products: selectedDesign.tagged_products || []
          }]
        }));
        toast.success("Design added to your appointment!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update selection");
    }
  };

  const isSelected = (portfolioId) => {
    return board?.selected_designs?.some(d => d.portfolio_id === portfolioId);
  };

  return (
    <div className="min-h-screen bg-white pb-32" data-testid="portfolio-page">
      {/* Header */}
      <header className="border-b border-gray-100 sticky top-0 z-40 bg-white/90 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-3 mb-4">
            <Link to="/board" className="text-gray-500 hover:text-black">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-display text-xl font-bold">Portfolio</h1>
            {board && (
              <Badge variant="outline" className="ml-auto">
                {board.selected_designs?.length || 0} selected
              </Badge>
            )}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              placeholder="Search designs, tags..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-12 rounded-full border-gray-200 bg-gray-50 focus:ring-black focus:border-black"
              data-testid="search-portfolio"
            />
          </div>
        </div>
      </header>

      {/* Portfolio Grid */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {loading ? (
          <div className="masonry-grid">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="masonry-item">
                <div className="border border-gray-200 rounded-2xl overflow-hidden">
                  <div className="bg-gray-100 animate-pulse" style={{ height: `${200 + Math.random() * 100}px` }} />
                  <div className="p-4">
                    <div className="h-4 bg-gray-100 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-gray-100 rounded w-1/2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : portfolio.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-600">No designs found</p>
          </div>
        ) : (
          <div className="masonry-grid">
            {portfolio.map((item) => (
              <div key={item.portfolio_id} className="masonry-item animate-fade-in" data-testid={`portfolio-${item.portfolio_id}`}>
                <div className={`border rounded-2xl overflow-hidden card-hover group ${isSelected(item.portfolio_id) ? 'border-black border-2' : 'border-gray-200'}`}>
                  {/* Image */}
                  <div className="relative overflow-hidden cursor-pointer" onClick={() => setSelectedItem(item)}>
                    <img
                      src={item.image_url}
                      alt={item.title}
                      className="w-full object-cover portfolio-image group-hover:scale-105 transition-transform duration-300"
                      style={{ minHeight: "180px" }}
                      loading="lazy"
                    />
                    
                    {/* Selection indicator */}
                    {isSelected(item.portfolio_id) && (
                      <div className="absolute top-3 right-3 w-8 h-8 bg-black rounded-full flex items-center justify-center shadow-lg">
                        <Check className="w-5 h-5 text-white" />
                      </div>
                    )}

                    {/* Modern hover overlay - just subtle darkening, tap to view */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-3 left-3 right-3">
                        <p className="text-white text-sm font-medium">{item.title}</p>
                        <p className="text-white/70 text-xs">Tap to view and select</p>
                      </div>
                    </div>
                  </div>

                  {/* Minimal Content - just title and quick add */}
                  <div className="p-3 flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-sm text-gray-900 truncate">{item.title}</h3>
                      {item.attributes?.shape && (
                        <p className="text-xs text-gray-600">{item.attributes.shape} • {item.attributes.length || 'Medium'}</p>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelect(item.portfolio_id);
                      }}
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                        isSelected(item.portfolio_id)
                          ? 'bg-black text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {isSelected(item.portfolio_id) ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Plus className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Detail Modal - Scrollable */}
      <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
        <DialogContent className="max-w-lg p-0 rounded-3xl overflow-hidden max-h-[90vh] flex flex-col">
          {selectedItem && (
            <>
              {/* Design Image - Fixed height */}
              <div className="relative flex-shrink-0">
                <img
                  src={selectedItem.image_url}
                  alt={selectedItem.title}
                  className="w-full h-48 object-cover"
                  onError={(e) => { e.target.src = "https://via.placeholder.com/400?text=Design"; }}
                />
              </div>
              
              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-5 space-y-4">
                  {/* Title and Description */}
                  <div>
                    <h2 className="font-display text-xl font-bold mb-1">{selectedItem.title}</h2>
                    {selectedItem.description && (
                      <p className="text-gray-700 text-sm">{selectedItem.description}</p>
                    )}
                  </div>

                  {/* Style Details - Compact */}
                  {selectedItem.attributes && (
                    <div className="flex flex-wrap gap-2">
                      {selectedItem.attributes.shape && (
                        <Badge className="bg-gray-100 text-gray-700 rounded-full text-xs">{selectedItem.attributes.shape}</Badge>
                      )}
                      {selectedItem.attributes.length && (
                        <Badge className="bg-gray-100 text-gray-700 rounded-full text-xs">{selectedItem.attributes.length}</Badge>
                      )}
                      {selectedItem.attributes.finish && (
                        <Badge className="bg-gray-100 text-gray-700 rounded-full text-xs">{selectedItem.attributes.finish}</Badge>
                      )}
                    </div>
                  )}

                  {/* Colors/Products Used - Clickable */}
                  {selectedItem.tagged_products?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-xs text-gray-600 uppercase tracking-wide mb-2">Colors Used</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedItem.tagged_products.map((product, idx) => (
                          <Link
                            key={idx}
                            to={`/colors?highlight=${product.product_code}`}
                            onClick={() => setSelectedItem(null)}
                            className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg hover:bg-gray-100 hover:shadow-sm transition-all cursor-pointer group"
                          >
                            <div
                              className="w-8 h-8 rounded-lg shadow-inner border border-gray-200 group-hover:scale-110 transition-transform"
                              style={{ backgroundColor: product.hex_code || product.hex || "#ccc" }}
                            />
                            <div className="min-w-0">
                              <p className="font-medium text-xs truncate group-hover:text-black">{product.name}</p>
                              <p className="text-[10px] text-gray-600">{product.brand}</p>
                            </div>
                            <span className="text-[10px] text-gray-500 group-hover:text-black">→</span>
                          </Link>
                        ))}
                      </div>
                      <p className="text-[10px] text-gray-500 mt-2">Tap a color to view and select</p>
                    </div>
                  )}

                  {/* Tagged Tools - Compact */}
                  {selectedItem.tagged_tools?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-xs text-gray-600 uppercase tracking-wide mb-2">Tools</h4>
                      <div className="flex flex-wrap gap-1">
                        {selectedItem.tagged_tools.map((tool, idx) => (
                          <Badge key={idx} variant="outline" className="rounded-full text-xs">{tool}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Technician Notes - Compact */}
                  {selectedItem.technician_notes && (
                    <div className="p-3 bg-gray-50 rounded-xl">
                      <p className="text-xs text-gray-700">{selectedItem.technician_notes}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Sticky Action Button */}
              <div className="flex-shrink-0 p-4 border-t bg-white">
                <Button
                  onClick={() => {
                    handleSelect(selectedItem.portfolio_id);
                    setSelectedItem(null);
                  }}
                  className={`w-full rounded-xl h-12 ${
                    isSelected(selectedItem.portfolio_id)
                      ? "bg-gray-100 hover:bg-gray-200 text-gray-900"
                      : "bg-black hover:bg-gray-900 text-white"
                  }`}
                >
                  {isSelected(selectedItem.portfolio_id) ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Selected - Tap to Remove
                    </>
                  ) : (
                    "Add to My Board"
                  )}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <MobileNav />
    </div>
  );
};

export default PortfolioPage;
